/**
 * AdminContenusPage — /admin/contenus (V9.14 Sprint 3 — Lot A)
 *
 * Cockpit institutionnel unique regroupant Curator, Sports Monitor et
 * articles contributeurs derrière une interface cohérente à 5 onglets :
 *   À traiter · En cours · Publiés · Mise en avant · Sources.
 *
 * Toutes les mutations réutilisent les endpoints existants
 * (curator, contrib_articles, admin_v8/moderation, admin_sports_monitor).
 * Aucune duplication de modèle : le backend `/api/admin/contents/*` agrège
 * les collections existantes en un DTO commun.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Inbox, ListChecks, CheckCircle2, Star, Rss, Search, RefreshCw, ShieldAlert,
  ArrowLeft, ExternalLink, Send, EyeOff, Pin, Home as HomeIcon, Archive, Trash2,
  Newspaper, Radio, User as UserIcon, Pencil, Sparkles,
  ThumbsUp, ThumbsDown, Loader2, X as XIcon,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ContentEditSheet } from "@/components/ContentEditSheet";

const TAB_META = {
  inbox:       { label: "À traiter",     icon: Inbox,       endpoint: "/admin/contents/inbox" },
  in_progress: { label: "En cours",       icon: ListChecks,  endpoint: "/admin/contents/in_progress" },
  published:   { label: "Publiés",        icon: CheckCircle2, endpoint: "/admin/contents/published" },
  featured:    { label: "Mise en avant",  icon: Star,        endpoint: "/admin/contents/featured" },
  sources:     { label: "Sources",        icon: Rss,         endpoint: "/admin/contents/sources" },
};

const KIND_ICON = {
  curator_item: Newspaper,
  user_article: UserIcon,
  sports_monitor_suggestion: Radio,
};

const KIND_LABEL = {
  curator_item: "Curator",
  user_article: "Contributeur",
  sports_monitor_suggestion: "Sports Monitor",
};

function relDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = Date.now();
  const diffH = (now - d.getTime()) / 3600000;
  if (diffH < 24) return `il y a ${Math.max(1, Math.round(diffH))} h`;
  if (diffH < 24 * 30) return `il y a ${Math.round(diffH / 24)} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
}

export default function AdminContenusPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const isModerator = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const initialTab = params.get("tab") || "inbox";
  const [tab, setTab] = useState(initialTab);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  // V9.17 Lot B : sélection multiple + panel latéral
  const [selected, setSelected] = useState(new Set());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetTarget, setSheetTarget] = useState(null); // {kind, id}

  // V13.14 — Fusion 3 modules : dialogs Publish options + Reject
  const [publishTarget, setPublishTarget] = useState(null); // item {kind,id,title,...}
  const [publishOpts, setPublishOpts] = useState({ homepage_visible: true, featured: false, pinned: false });
  const [publishBusy, setPublishBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);   // item
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);

  const load = useCallback(async () => {
    if (!isModerator) return;
    setLoading(true);
    try {
      const meta = TAB_META[tab] || TAB_META.inbox;
      const [dataRes, statsRes] = await Promise.all([
        api.get(meta.endpoint, { params: q ? { q, limit: 100 } : { limit: 100 } }),
        api.get("/admin/contents/stats").catch(() => ({ data: { counts: {} } })),
      ]);
      setItems(dataRes.data.items || []);
      setCounts(statsRes.data.counts || {});
    } catch (e) { showError(e, { prefix: "Cockpit :" }); }
    finally { setLoading(false); }
  }, [isModerator, tab, q, refreshKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Sync tab -> URL
    if (tab !== params.get("tab")) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // ---------- Actions rapides (réutilisent endpoints existants) ----------
  const kindToEntity = (kind) => ({
    curator_item: "curator_item",
    user_article: "user_article",
  }[kind]);

  const doAction = async (item, action) => {
    try {
      const entity = kindToEntity(item.kind);
      if (!entity) {
        toast.error("Cette action n'est pas disponible pour ce type d'entrée.");
        return;
      }
      // Réutilise l'endpoint unifié Sprint 1 : /api/admin/entities/{entity}/{id}/{action}
      await api.post(`/admin/entities/${entity}/${item.id}/${action}`, {});
      toast.success(actionLabel(action) + " · " + (item.title || item.id));
      refresh();
    } catch (e) { showError(e, { prefix: actionLabel(action) + " :" }); }
  };

  // V13.14 — Publier via dialog options (fusion AdminCuratorPage.PublishOptionsDialog)
  const openPublishDialog = (item) => {
    setPublishTarget(item);
    // Défaut UX : mettre sur la home ET dans les Actualités (bug remonté fixé)
    setPublishOpts({ homepage_visible: true, featured: false, pinned: false });
  };

  const doPublishWithOpts = async () => {
    if (!publishTarget) return;
    setPublishBusy(true);
    try {
      if (publishTarget.kind === "curator_item") {
        await api.post(`/curator/items/${publishTarget.id}/publish`, publishOpts);
      } else if (publishTarget.kind === "user_article") {
        // Contrib article : approve → published, puis on ajuste les flags si demandé
        await api.post(`/contrib/articles/${publishTarget.id}/admin/approve`);
        if (publishOpts.pinned || publishOpts.featured) {
          try {
            await api.post(`/contrib/articles/${publishTarget.id}/admin/homepage`, {
              pinned: !!publishOpts.pinned,
              featured: !!publishOpts.featured,
            });
          } catch { /* pas bloquant */ }
        }
        // homepage_visible=false : le backend contrib_articles admin_approve met true par défaut,
        // on force à false si l'admin l'a explicitement décoché
        if (!publishOpts.homepage_visible) {
          try {
            await api.patch(`/contrib/articles/${publishTarget.id}`, { homepage_visible: false });
          } catch { /* pas bloquant */ }
        }
      }
      toast.success("Publié · " + (publishTarget.title || publishTarget.id));
      setPublishTarget(null);
      refresh();
    } catch (e) {
      showError(e, { prefix: "Publier :" });
    } finally {
      setPublishBusy(false);
    }
  };

  // V13.14 — Refuser avec raison (fusion AdminContribArticlesPage + AdminCuratorPage)
  const openRejectDialog = (item) => {
    setRejectTarget(item);
    setRejectReason("");
  };

  const doReject = async () => {
    if (!rejectTarget) return;
    setRejectBusy(true);
    const reason = rejectReason.trim() || "Non précisé";
    try {
      if (rejectTarget.kind === "curator_item") {
        await api.post(`/curator/items/${rejectTarget.id}/reject`, { note: reason });
      } else if (rejectTarget.kind === "user_article") {
        await api.post(`/contrib/articles/${rejectTarget.id}/admin/reject`, { reason });
      } else if (rejectTarget.kind === "sports_monitor_suggestion") {
        // Dismiss / archive de la suggestion
        await api.post(`/admin/entities/sports_monitor_suggestion/${rejectTarget.id}/archive`, {}).catch(() => {});
      }
      toast.success("Refusé · " + (rejectTarget.title || rejectTarget.id));
      setRejectTarget(null);
      setRejectReason("");
      refresh();
    } catch (e) {
      showError(e, { prefix: "Refuser :" });
    } finally {
      setRejectBusy(false);
    }
  };

  const toggleHomepage = async (item, on) => {
    try {
      if (item.kind === "curator_item") {
        await api.post(`/curator/items/${item.id}/homepage`, { homepage_visible: on });
      } else {
        // user_article : utilise l'endpoint moderation générique s'il existe
        await api.patch(`/contrib/articles/${item.id}`, { homepage_visible: on });
      }
      toast.success(on ? "Ajouté à la home." : "Retiré de la home.");
      refresh();
    } catch (e) { showError(e, { prefix: "Home :" }); }
  };

  // V12.x — Désépingler ou épingler un contenu « à la une »
  const togglePinned = async (item, on) => {
    try {
      if (item.kind === "curator_item") {
        await api.patch(`/curator/items/${item.id}`, { pinned: on });
      } else {
        await api.post(`/contrib/articles/${item.id}/admin/homepage`, { pinned: on });
      }
      toast.success(on ? "Article épinglé à la une." : "Article désépinglé.");
      refresh();
    } catch (e) { showError(e, { prefix: "Épingler :" }); }
  };

  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="cockpit-loading-auth">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (!isModerator) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="cockpit-access-denied">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">Le cockpit éditorial est réservé aux admins et à la COPF.</p>
          {user && <p className="text-xs text-muted-foreground mt-2">Rôles actuels : {(user.roles || []).join(", ") || "aucun"}</p>}
          <Button className="mt-4" variant="outline" onClick={() => nav("/")}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />Retour
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-contenus-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-display font-bold">Cockpit Contenus</h1>
            <Badge className="bg-[hsl(var(--aito-gold))] text-black text-[10px] uppercase tracking-wider">
              <Sparkles className="h-3 w-3 mr-1" />Unifié
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Curator · Articles contributeurs · Revue de presse — un seul écran pour l'ensemble du workflow éditorial.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") refresh(); }}
              placeholder="Rechercher titre / résumé…"
              className="pl-8 w-64"
              data-testid="cockpit-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="cockpit-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Rafraîchir
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="cockpit-tabs" className="w-full justify-start overflow-x-auto no-scrollbar">
          {Object.entries(TAB_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const count = counts[key];
            return (
              <TabsTrigger key={key} value={key} data-testid={`cockpit-tab-${key}`}>
                <Icon className="h-3.5 w-3.5 mr-1.5" />
                {meta.label}
                {count !== undefined && count !== null && (
                  <Badge variant="outline" className="ml-1.5 text-[10px] font-mono">{count}</Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.keys(TAB_META).map((key) => (
          <TabsContent key={key} value={key} className="mt-4">
            {tab === "sources" ? (
              <SourcesTable items={items} loading={loading} />
            ) : (
              <>
                <BulkActionBar
                  selected={selected}
                  items={items}
                  onClear={() => setSelected(new Set())}
                  onDone={refresh}
                />
                <ContentTable
                  items={items}
                  loading={loading}
                  tab={tab}
                  selected={selected}
                  onToggleSelect={(it) => setSelected((cur) => {
                    const next = new Set(cur);
                    const key = `${it.kind}:${it.id}`;
                    if (next.has(key)) next.delete(key); else next.add(key);
                    return next;
                  })}
                  onToggleAll={() => setSelected((cur) => {
                    const allKeys = items.map((x) => `${x.kind}:${x.id}`);
                    const allSelected = allKeys.every((k) => cur.has(k));
                    return new Set(allSelected ? [] : allKeys);
                  })}
                  onOpenPanel={(it) => { setSheetTarget({ kind: it.kind, id: it.id }); setSheetOpen(true); }}
                  onPublish={openPublishDialog}
                  onReject={openRejectDialog}
                  onAction={doAction}
                  onToggleHomepage={toggleHomepage}
                  onTogglePinned={togglePinned}
                />
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* V9.17 Lot B : Panneau latéral d'édition */}
      {sheetTarget && (
        <ContentEditSheet
          open={sheetOpen}
          onOpenChange={(v) => { setSheetOpen(v); if (!v) setSheetTarget(null); }}
          kind={sheetTarget.kind}
          itemId={sheetTarget.id}
          list={items.filter((x) => x.kind !== "sports_monitor_suggestion")}
          onNavigate={(next) => setSheetTarget({ kind: next.kind, id: next.id })}
          onChanged={refresh}
        />
      )}

      {/* V13.14 — Dialog Publier avec options (fusion AdminCuratorPage) */}
      <Dialog open={!!publishTarget} onOpenChange={(open) => { if (!open) setPublishTarget(null); }}>
        <DialogContent data-testid="cockpit-publish-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Publier ce contenu</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">« {publishTarget?.title || "sans titre"} »</strong><br />
            L'article sera publié dans <strong>Actualités</strong> et sur la <strong>page d'accueil</strong> par défaut. Décoche les options ci-dessous si nécessaire.
          </p>
          <div className="space-y-2 py-2">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!publishOpts.homepage_visible}
                onCheckedChange={(v) => setPublishOpts((o) => ({ ...o, homepage_visible: !!v }))}
                data-testid="cockpit-publish-homepage"
              />
              <span>
                <strong>Afficher sur la page d'accueil</strong>
                <span className="block text-xs text-muted-foreground">Visible dans la section « Actualités récentes » (30 j)</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!publishOpts.featured}
                onCheckedChange={(v) => setPublishOpts((o) => ({ ...o, featured: !!v }))}
                data-testid="cockpit-publish-featured"
              />
              <span>
                <strong>Mettre en avant (★)</strong>
                <span className="block text-xs text-muted-foreground">Visible sans limite de temps dans « À la une »</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!publishOpts.pinned}
                onCheckedChange={(v) => setPublishOpts((o) => ({ ...o, pinned: !!v }))}
                data-testid="cockpit-publish-pinned"
              />
              <span>
                <strong>Épingler en priorité (📌)</strong>
                <span className="block text-xs text-muted-foreground">Toujours en tête de la page d'accueil</span>
              </span>
            </label>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setPublishTarget(null)} disabled={publishBusy} data-testid="cockpit-publish-cancel">
              Annuler
            </Button>
            <Button
              onClick={doPublishWithOpts}
              disabled={publishBusy}
              className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black"
              data-testid="cockpit-publish-confirm"
            >
              {publishBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              Publier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V13.14 — Dialog Refuser avec raison (fusion AdminContribArticlesPage) */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent data-testid="cockpit-reject-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Refuser ce contenu</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">« {rejectTarget?.title || "sans titre"} »</strong>
          </p>
          <div className="space-y-2 py-2">
            <Label htmlFor="cockpit-reject-reason" className="text-xs">Motif du refus (visible par l'auteur)</Label>
            <Textarea
              id="cockpit-reject-reason"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex. : Hors-sujet sportif polynésien, source non vérifiable, ton inapproprié…"
              data-testid="cockpit-reject-reason"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => { setRejectTarget(null); setRejectReason(""); }} disabled={rejectBusy} data-testid="cockpit-reject-cancel">
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={doReject}
              disabled={rejectBusy}
              data-testid="cockpit-reject-confirm"
            >
              {rejectBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ThumbsDown className="h-4 w-4 mr-1.5" />}
              Refuser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function actionLabel(a) {
  return {
    hide: "Dépublié",
    archive: "Archivé",
    delete: "Supprimé",
    restore: "Restauré",
    unpublish: "Dépublié",
  }[a] || a;
}

// ---------- Table Contenus ----------
function ContentTable({ items, loading, tab, selected, onToggleSelect, onToggleAll, onOpenPanel, onPublish, onReject, onAction, onToggleHomepage, onTogglePinned }) {
  if (loading) {
    return (
      <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
    );
  }
  if (items.length === 0) {
    return <Card className="p-8 text-center text-sm text-muted-foreground">Aucun contenu à afficher dans cet onglet.</Card>;
  }

  const selectableItems = items.filter((x) => x.kind !== "sports_monitor_suggestion");
  const allSelected = selectableItems.length > 0 && selectableItems.every((x) => selected.has(`${x.kind}:${x.id}`));

  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8"><Checkbox checked={allSelected} onCheckedChange={onToggleAll} aria-label="tout sélectionner" data-testid="cockpit-select-all" /></TableHead>
            <TableHead className="w-32">Type</TableHead>
            <TableHead>Titre</TableHead>
            <TableHead className="hidden md:table-cell w-32">Discipline</TableHead>
            <TableHead className="hidden md:table-cell w-28">Date</TableHead>
            <TableHead className="hidden lg:table-cell w-40">Statut</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => {
            const Icon = KIND_ICON[it.kind] || Newspaper;
            const selectable = it.kind !== "sports_monitor_suggestion";
            const rowKey = `${it.kind}:${it.id}`;
            return (
              <TableRow key={`${it.kind}-${it.id}`} data-testid={`cockpit-row-${it.id}`} className="hover:bg-secondary/40">
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {selectable && (
                    <Checkbox checked={selected.has(rowKey)} onCheckedChange={() => onToggleSelect(it)} data-testid={`cockpit-select-${it.id}`} />
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px]">
                    <Icon className="h-3 w-3 mr-1" />{KIND_LABEL[it.kind] || it.kind}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-start gap-2 min-w-0">
                    {it.image_url && (
                      <img src={it.image_url} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className="h-9 w-9 rounded object-cover shrink-0 border" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate max-w-[42ch]" title={it.title}>{it.title || "(sans titre)"}</div>
                      <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                        {it.url && (
                          <a href={it.url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-0.5">
                            {it.source_name || "Source"} <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        )}
                        {!it.url && it.source_name && <span>{it.source_name}</span>}
                        {it.author && <span>· {it.author}</span>}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs hidden md:table-cell capitalize">
                  {it.discipline ? String(it.discipline).replace(/-/g, " ") : "—"}
                </TableCell>
                <TableCell className="text-xs hidden md:table-cell">{relDate(it.published_at || it.created_at)}</TableCell>
                <TableCell className="text-xs hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-[10px]">{it.status || "—"}</Badge>
                    {it.pinned && <Badge className="bg-[hsl(var(--aito-gold))] text-black text-[10px]"><Pin className="h-2.5 w-2.5 mr-0.5" />pin</Badge>}
                    {it.featured && !it.pinned && <Badge className="bg-[hsl(var(--aito-lagoon))] text-black text-[10px]">★ fea</Badge>}
                    {it.homepage_visible && <Badge variant="outline" className="text-[10px]"><HomeIcon className="h-2.5 w-2.5 mr-0.5" />home</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1 justify-end">
                    {selectable && (
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onOpenPanel(it)} data-testid={`cockpit-open-${it.id}`} title="Ouvrir dans le panneau">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {tab === "inbox" && (it.kind === "curator_item" || it.kind === "user_article") && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => onPublish(it)}
                          data-testid={`cockpit-publish-${it.id}`}
                          title="Publier avec options (accueil, à la une, épinglé)"
                        >
                          <ThumbsUp className="h-3.5 w-3.5 mr-1" />Publier
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-destructive hover:bg-destructive/10"
                          onClick={() => onReject?.(it)}
                          data-testid={`cockpit-reject-${it.id}`}
                          title="Refuser avec motif"
                        >
                          <ThumbsDown className="h-3.5 w-3.5 mr-1" />Refuser
                        </Button>
                      </>
                    )}
                    {tab === "published" && (
                      <>
                        {!it.homepage_visible && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onToggleHomepage(it, true)} data-testid={`cockpit-home-on-${it.id}`}>
                            <HomeIcon className="h-3.5 w-3.5 mr-1" />+home
                          </Button>
                        )}
                        {it.homepage_visible && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onToggleHomepage(it, false)} data-testid={`cockpit-home-off-${it.id}`}>
                            <EyeOff className="h-3.5 w-3.5 mr-1" />-home
                          </Button>
                        )}
                        {/* V12.x — Épingler / désépingler */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className={`h-7 px-2 ${it.pinned ? "text-[hsl(var(--aito-gold-strong,var(--aito-gold)))]" : ""}`}
                          onClick={() => onTogglePinned?.(it, !it.pinned)}
                          title={it.pinned ? "Désépingler de la une" : "Épingler à la une"}
                          data-testid={`cockpit-pin-toggle-${it.id}`}
                        >
                          <Pin className="h-3.5 w-3.5 mr-1" />
                          {it.pinned ? "Détacher" : "Épingler"}
                        </Button>
                      </>
                    )}
                    {tab === "featured" && it.pinned && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-amber-800 hover:bg-amber-50"
                        onClick={() => onTogglePinned?.(it, false)}
                        title="Désépingler"
                        data-testid={`cockpit-featured-unpin-${it.id}`}
                      >
                        <Pin className="h-3.5 w-3.5 mr-1" /> Détacher
                      </Button>
                    )}
                    {(tab === "published" || tab === "in_progress" || tab === "featured") && kindToEntity(it.kind) && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-700 hover:bg-amber-50" onClick={() => onAction(it, "archive")} data-testid={`cockpit-archive-${it.id}`}>
                          <Archive className="h-3.5 w-3.5 mr-1" />Archiver
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:bg-destructive/10" onClick={() => onAction(it, "delete")} data-testid={`cockpit-delete-${it.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

// Helper (extraite pour clarté)
function kindToEntity(kind) {
  return ({ curator_item: "curator_item", user_article: "user_article" })[kind];
}

// ---------- Table Sources ----------
function SourcesTable({ items, loading }) {
  if (loading) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  if (items.length === 0) return <Card className="p-8 text-center text-sm text-muted-foreground">Aucune source configurée.</Card>;
  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Source</TableHead>
            <TableHead className="w-32">Type</TableHead>
            <TableHead className="w-32">Status</TableHead>
            <TableHead className="hidden md:table-cell w-40">Dernière collecte</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((s) => (
            <TableRow key={s.id} data-testid={`cockpit-source-${s.id}`}>
              <TableCell>
                <div className="font-medium truncate max-w-[36ch]">{s.name || s.url}</div>
                {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground hover:underline">{s.url}</a>}
              </TableCell>
              <TableCell><Badge variant="outline" className="text-[10px]">{s.type || s.kind || "rss"}</Badge></TableCell>
              <TableCell><Badge variant="outline" className="text-[10px]">{s.status || "active"}</Badge></TableCell>
              <TableCell className="text-xs hidden md:table-cell">{s.last_run_at ? relDate(s.last_run_at) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ---------- Bulk Action Bar (V9.17 Sprint 3 Lot B) ----------
function BulkActionBar({ selected, items, onClear, onDone }) {
  const [busy, setBusy] = useState(false);
  const [tagInput, setTagInput] = useState("");
  if (selected.size === 0) return null;

  const pickItems = () => Array.from(selected).map((k) => {
    const [kind, id] = k.split(":");
    return { kind, id };
  });

  const doBulk = async (action, payload = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/contents/bulk-action", { items: pickItems(), action, ...payload });
      if (data.error_count === 0) {
        toast.success(`${data.applied_count} contenu(s) traité(s).`);
      } else {
        toast.warning(`${data.applied_count} réussis, ${data.error_count} en erreur — voir la console.`);
        console.warn("Bulk errors:", data.errors);
      }
      onClear();
      onDone();
    } catch (e) { showError(e, { prefix: "Actions groupées :" }); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-3 mb-3 bg-secondary/40 border-dashed" data-testid="cockpit-bulk-bar">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{selected.size} sélectionné(s)</span>
        <Button size="sm" variant="outline" onClick={() => doBulk("publish")} disabled={busy} data-testid="bulk-publish"><Send className="h-3.5 w-3.5 mr-1" />Publier</Button>
        <Button size="sm" variant="outline" onClick={() => doBulk("unpublish")} disabled={busy} data-testid="bulk-unpublish"><EyeOff className="h-3.5 w-3.5 mr-1" />Dépublier</Button>
        <Button size="sm" variant="outline" onClick={() => doBulk("home_show")} disabled={busy} data-testid="bulk-home-show"><HomeIcon className="h-3.5 w-3.5 mr-1" />Afficher sur home</Button>
        <Button size="sm" variant="outline" onClick={() => doBulk("home_hide")} disabled={busy} data-testid="bulk-home-hide"><EyeOff className="h-3.5 w-3.5 mr-1" />Retirer de home</Button>
        <Button size="sm" variant="outline" onClick={() => doBulk("archive")} disabled={busy} data-testid="bulk-archive"><Archive className="h-3.5 w-3.5 mr-1" />Archiver</Button>
        <div className="inline-flex items-center gap-1">
          <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="tag" className="h-8 w-32" data-testid="bulk-tag-input" />
          <Button size="sm" variant="outline" disabled={busy || !tagInput.trim()} onClick={() => doBulk("add_tag", { tag: tagInput.trim() })} data-testid="bulk-add-tag">Ajouter tag</Button>
        </div>
        <Button size="sm" variant="ghost" onClick={onClear} className="ml-auto">Annuler la sélection</Button>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        Actions interdites en masse : purge, fusion, suppression définitive.
      </div>
    </Card>
  );
}
